import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, ChevronRight, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useActiveClassificationScanReadOnly } from '../../hooks/useActiveClassificationScan';
import { useLanguage } from '../../contexts/LanguageContext';

const SCAN_TYPE_LABELS = {
  full: 'scan.typeFull',
  incremental: 'scan.typeIncremental',
  source_specific: 'scan.typeSource',
  source: 'scan.typeSource',
};

/**
 * Floating progress pill rendered in AppLayout. Visible from every
 * authenticated page whenever a classification scan is in progress.
 * Click → jumps to the classification page so the user can see the full
 * progress bar and cancel the scan if needed.
 *
 * Hidden when the user is already on /classification (the page-level
 * progress bar covers that view) to avoid double-rendering the same info.
 */
const ActiveScanIndicator = () => {
  const { activeScan, isActive } = useActiveClassificationScanReadOnly();
  const { t } = useTranslation('classification');
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const onClassificationPage = location.pathname.startsWith('/classification');
  const visible = isActive && !onClassificationPage;

  const progress = Math.max(0, Math.min(100, Math.round(activeScan?.progressPercent || 0)));
  const status = activeScan?.status || 'scanning';
  const scanTypeKey = SCAN_TYPE_LABELS[activeScan?.scanType] || 'scan.typeFull';
  const fieldsFound = activeScan?.fieldsFound || 0;

  // AnimatePresence MUST wrap the conditional (not the other way around) so it
  // can track mount/unmount and run the exit animation. The motion child also
  // needs a stable `key`. Otherwise we get random click-handling glitches and
  // (rarely) a stuck mid-animation state.
  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          key="active-scan-indicator"
          type="button"
          onClick={() => navigate('/classification')}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.18 }}
          dir={isRTL ? 'rtl' : 'ltr'}
          className={`fixed bottom-4 z-40 ${isRTL ? 'left-4' : 'right-4'}
            flex items-center gap-3 ps-3 pe-2 py-2 rounded-full
            bg-white shadow-lg border border-emerald-200 hover:border-emerald-400
            hover:shadow-xl transition-all min-w-[260px] max-w-sm group cursor-pointer
            focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500`}
          aria-label={t('activeScanIndicatorAria', 'Classification scan in progress — click to view')}
          title={t('activeScanIndicatorTitle', 'Click to open classification scan')}
        >
          <div className="relative shrink-0 w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center">
            <Search size={14} className="text-emerald-600" />
            <Loader2 size={32} className="absolute inset-0 m-auto animate-spin text-emerald-500/60" />
          </div>
          <div className="flex-1 min-w-0 text-start">
            <div className="text-xs font-semibold text-slate-900 truncate flex items-center gap-1.5">
              <span>{t('activeScanIndicatorTitle', 'Classification scan')}</span>
              <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-emerald-50 text-emerald-700 border border-emerald-100">
                {t(scanTypeKey, activeScan?.scanType || 'full')}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-[10px] font-mono text-slate-500 tabular-nums shrink-0">
                {progress}%
              </span>
            </div>
            <div className="mt-0.5 text-[10px] text-slate-500 truncate">
              {t(`scan.statusLabel.${status}`, status)}
              {fieldsFound > 0 && (
                <span className="ms-1">· {fieldsFound} {t('fieldsFound', 'fields')}</span>
              )}
            </div>
          </div>
          <ChevronRight
            size={14}
            className={`shrink-0 text-slate-400 group-hover:text-emerald-600 transition-transform ${isRTL ? 'rotate-180' : ''}`}
          />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export default ActiveScanIndicator;
